"use client"

import { motion } from "motion/react"
import { HomeIcon, ArrowRight, Workflow, Cpu } from "lucide-react"
import Link from "next/link"
import { useParams } from "next/navigation"

import { useServiceLanguage } from "@/core/hooks/use-service-language"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/core/ui/breadcrumb"
import { Button } from "@/core/ui/button"
import SpotlightCard from "@/core/ui/effects/SpotlightCard"
import { getServiceTheme } from "@/core/utils/service-theme"
import { RelatedContentForService } from "@/modules/marketing/services/components/related-content-for-service"
import SubServiceHero from "@/modules/marketing/services/components/sub-service-hero"

export default function SubServicePage() {
    const params = useParams<{ category: string; subservice: string }>()
    const { getServiceData, getSubServiceData, getAllSubServicesForCategory } = useServiceLanguage()

    const serviceData = getServiceData(params.category as string)
    const subServiceData = getSubServiceData(params.category as string, params.subservice as string)

    if (!serviceData || !subServiceData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black text-white">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
                    <Button asChild variant="outline"><Link href="/services">Back to Services</Link></Button>
                </div>
            </div>
        )
    }

    const theme = getServiceTheme(params.category as string)

    const relatedSubServices = getAllSubServicesForCategory(params.category as string)
        .filter((service) => service.id !== params.subservice)
        .slice(0, 3)

    // --- Category Specific Renders ---

    const renderFeatures = () => {
        // 1. BLOCKCHAIN: Linked Chain Layout
        if (params.category === 'blockchain') {
            return (
                <div className="relative">
                    <div className="absolute left-8 top-0 bottom-0 w-px bg-cyan-900/50 dashed border-l-2 border-dashed border-cyan-900/50" />
                    <div className="space-y-6">
                        {subServiceData.features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="relative pl-24"
                            >
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-16 h-px bg-cyan-800" />
                                <div className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 bg-black border border-cyan-500 rotate-45 z-10" />

                                <SpotlightCard
                                    className="bg-black/80 border border-white/10 hover:border-cyan-500/50 transition-all p-6 font-mono relative overflow-hidden group"
                                    spotlightColor="rgba(34, 211, 238, 0.1)"
                                >
                                    <div className="absolute top-0 right-0 p-2 text-[10px] text-cyan-900 font-bold group-hover:text-cyan-500/50 transition-colors">
                                        BLOCK_0{index + 1}
                                    </div>
                                    <h3 className="text-lg font-bold text-cyan-400 mb-2">{`>>`} FEATURE_0{index + 1}</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed border-l border-white/10 pl-4">{feature}</p>
                                </SpotlightCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )
        }

        // 2. GENAI: Neural Mesh Layout (Masonry-ish)
        if (params.category === 'genai') {
            return (
                <div className="grid md:grid-cols-2 gap-8 relative">
                    {/* Background Connections */}
                    <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
                        <svg className="w-full h-full">
                            <path d="M100,100 Q400,300 700,100" fill="none" stroke="#a855f7" strokeWidth="2" />
                            <path d="M100,400 Q400,200 700,400" fill="none" stroke="#a855f7" strokeWidth="2" />
                        </svg>
                    </div>

                    {subServiceData.features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className={`${index % 2 === 1 ? 'md:mt-12' : ''} relative z-10`}
                        >
                            <SpotlightCard
                                className="h-full bg-white/3 border-purple-500/20 rounded-3xl p-8 backdrop-blur-xl hover:bg-purple-900/10 transition-all"
                                spotlightColor="rgba(168, 85, 247, 0.2)"
                            >
                                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-6 shadow-[0_0_15px_rgba(168,85,247,0.3)]">
                                    <Cpu className="w-6 h-6 text-purple-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">Node {index + 1}</h3>
                                <p className="text-gray-300 leading-relaxed">{feature}</p>
                            </SpotlightCard>
                        </motion.div>
                    ))}
                </div>
            )
        }

        // 3. ENTERPRISE: Dashboard Grid
        if (params.category === 'enterprise') {
            return (
                <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden">
                    {subServiceData.features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-black p-8 hover:bg-white/2 transition-colors relative group"
                        >
                            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/5">
                                <span className="text-xs font-semibold text-blue-500 uppercase tracking-wider">Metric 0{index + 1}</span>
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Capability Check</h3>
                            <p className="text-gray-400 text-sm">{feature}</p>

                            <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                        </motion.div>
                    ))}
                </div>
            )
        }

        // 4. DEVELOPMENT: Default Cards
        return (
            <div className="grid md:grid-cols-2 gap-6">
                {subServiceData.features.map((feature, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                    >
                        <SpotlightCard
                            className={`h-full bg-[#111] border-white/10 p-8 flex items-start gap-6 hover:border-emerald-500/30 transition-colors group`}
                            spotlightColor={`${theme.hex}26`}
                        >
                            <div className="text-emerald-500 font-mono text-xl font-bold">
                                {`{`}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">feature: &quot;0{index + 1}&quot;</h3>
                                <p className="text-gray-400 leading-relaxed font-mono text-sm">
                                    <span className="text-gray-600">{'//'}</span> {feature}
                                </p>
                            </div>
                            <div className="mt-auto self-end text-emerald-500 font-mono text-xl font-bold">
                                {`}`}
                            </div>
                        </SpotlightCard>
                    </motion.div>
                ))}
            </div>
        )
    }

    const renderProcess = () => {
        // 1. BLOCKCHAIN: Transaction Log
        if (params.category === 'blockchain') {
            return (
                <div className="font-mono space-y-2">
                    {subServiceData.process.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center gap-4 p-4 border border-white/10 bg-black/40 hover:bg-cyan-900/10 transition-colors"
                        >
                            <div className="text-cyan-500 w-24 text-xs opacity-50">
                                {new Date().getFullYear()}-{(index + 1).toString().padStart(2, '0')}
                            </div>
                            <div className="text-cyan-400 font-bold">
                                [TX_HASH_{index}]
                            </div>
                            <div className="h-px bg-white/10 flex-1" />
                            <div className="text-gray-300 text-sm">
                                {step}
                            </div>
                            <div className="text-green-500 text-xs px-2 py-1 bg-green-500/10 rounded">
                                CONFIRMED
                            </div>
                        </motion.div>
                    ))}
                </div>
            )
        }

        // 2. GENAI: Circular/Iterative Flow
        if (params.category === 'genai') {
            return (
                <div className="relative max-w-3xl mx-auto py-12">
                    <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-linear-to-b from-purple-500/0 via-purple-500/50 to-purple-500/0 -translate-x-1/2" />

                    {subServiceData.process.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className={`flex items-center gap-8 mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse text-right'}`}
                        >
                            <div className="flex-1">
                                <h3 className="text-purple-400 text-xl font-bold mb-2">Phase {index + 1}</h3>
                                <p className="text-gray-300">{step}</p>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-black border-4 border-purple-500 z-10 shadow-[0_0_20px_rgba(168,85,247,0.5)] flex items-center justify-center text-xs font-bold">
                                {index + 1}
                            </div>
                            <div className="flex-1" />
                        </motion.div>
                    ))}
                </div>
            )
        }

        // Default Vertical Timeline for others
        return (
            <div className="relative">
                <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2 hidden md:block" />
                <div className="space-y-12 md:space-y-24">
                    {subServiceData.process.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            className={`flex flex-col md:flex-row gap-8 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
                        >
                            <div className="flex-1 w-full">
                                <SpotlightCard className={`p-8 bg-black/40 border-white/10 backdrop-blur-md hover:${theme.accentBorder} transition-colors`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className={`text-2xl font-bold ${theme.accentColor}`}>Phase {index + 1}</h3>
                                        <Workflow className="text-white/20 h-6 w-6" />
                                    </div>
                                    <p className="text-gray-400 text-lg">{step}</p>
                                </SpotlightCard>
                            </div>

                            <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 border-black text-white font-bold text-sm shadow-lg shrink-0" style={{ backgroundColor: theme.hex, boxShadow: `0 0 20px ${theme.hex}80` }}>
                                {index + 1}
                            </div>

                            <div className="flex-1 w-full hidden md:block" />
                        </motion.div>
                    ))}
                </div>
            </div>
        )
    }

    const getCtaLabel = (category: string) => {
        switch (category) {
            case 'blockchain': return "Discuss Blockchain Strategy"
            case 'genai': return "Explore AI Solutions"
            case 'enterprise': return "Architect Your System"
            case 'development': return "Start Building Now"
            default: return "Start Your Project"
        }
    }

    return (
        <div className={`min-h-screen bg-black text-white ${theme.selection}`}>

            {/* Navigation Breadcrumb */}
            <div className="absolute top-24 left-0 w-full z-20 px-4 pointer-events-none">
                <div className="container mx-auto pointer-events-auto">
                    <Breadcrumb className="text-muted-foreground/40 hover:text-white/80 transition-colors inline-block backdrop-blur-xs px-4 py-2 rounded-full border border-white/5 bg-black/20">
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/" className="hover:text-white transition-colors">
                                <HomeIcon className="mr-2 h-3 w-3" />
                                Home
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/services" className="hover:text-white transition-colors">Services</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink href={`/services/${params.category}`} className="hover:text-white transition-colors">
                                {serviceData.title}
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink href={`/services/${params.category}/${params.subservice}`} className={`${theme.breadcrumbActive} font-medium`}>
                                {subServiceData.title}
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </div>
            </div>

            <SubServiceHero
                title={subServiceData.title}
                description={subServiceData.description}
                icon={subServiceData.icon}
                categoryTitle={serviceData.title}
                theme={theme}
            />

            <div id="details" className="container mx-auto px-4 py-24">

                {/* Features Section */}
                <div className="mb-32">
                    <div className="flex items-center gap-4 mb-12">
                        <div className="h-px bg-linear-to-r from-white/10 to-transparent flex-1" />
                        <span className={`${theme.accentColor.replace('text-', 'text-')} font-mono text-sm tracking-widest uppercase`}>
                            {'//'} SYSTEM_CAPABILITIES
                        </span>
                        <div className="h-px bg-linear-to-l from-white/10 to-transparent flex-1" />
                    </div>

                    {renderFeatures()}
                </div>

                {/* Process Section */}
                <div className="mb-32">
                    <div className="flex flex-col items-center mb-16 text-center">
                        <span className={`${theme.accentColor.replace('text-', 'text-')} font-mono text-sm tracking-widest uppercase mb-4`}>
                            {'//'} EXECUTION_PIPELINE
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-white">Process Flow</h2>
                    </div>

                    {renderProcess()}
                </div>

                {/* Related Blog Posts */}
                <div className="mb-32">
                    <RelatedContentForService
                        serviceId={params.subservice as string}
                        categoryId={params.category as string}
                    />
                </div>

                {/* CTA */}
                <div className="relative rounded-3xl overflow-hidden p-12 text-center border border-white/10 bg-white/2">
                    <div className="relative z-10 max-w-2xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Initialize {subServiceData.title}</h2>
                        <p className="text-lg text-gray-400 mb-8">
                            Ready to deploy this module? Let&apos;s discuss the technical requirements and implementation strategy.
                        </p>
                        <Button asChild size="lg" className="rounded-full px-8 text-lg text-white hover:opacity-90 group" style={{ backgroundColor: theme.hex }}>
                            <Link href="/contact" className="flex items-center gap-2">
                                <span>{getCtaLabel(params.category as string)}</span>
                                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Button>
                    </div>

                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] blur-[120px] rounded-full" style={{ backgroundColor: theme.hex }} />
                    </div>
                </div>

            </div>

            {/* Related Services Footer */}
            {relatedSubServices.length > 0 && (
                <div className="border-t border-white/10 py-16 bg-black">
                    <div className="container mx-auto px-4">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-xl font-bold text-gray-400">Related Modules</h3>
                            <Link href={`/services/${params.category}`} className={`text-sm ${theme.accentColor} hover:opacity-80`}>
                                View All
                            </Link>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {relatedSubServices.map((service) => (
                                <Link key={service.id} href={`/services/${params.category}/${service.id}`} className="group block h-full">
                                    <div className={`h-full p-6 bg-white/5 border border-white/10 rounded-xl hover:${theme.accentBorder} transition-all hover:bg-white/10`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className={`text-lg font-bold text-white group-hover:${theme.accentColor} transition-colors`}>{service.title}</h3>
                                            <ArrowRight className={`h-4 w-4 text-white/30 group-hover:${theme.accentColor} group-hover:translate-x-1 transition-all`} />
                                        </div>
                                        <p className="text-gray-500 text-sm line-clamp-2">{service.shortDescription}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}
